import 'virtual:svg-icons-register'


// import { indexStore } from '@/stores/index'

// import { messagesDe } from '@/locales/de';
// import { en } from '@/locales/en';

// Create
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createVfm } from 'vue-final-modal'
import { createRouter, createWebHistory } from 'vue-router'
import { setupI18n } from '@/i18n'
import {
  createHead,
  VueHeadMixin // The options API functionality is only provided out-of-the-box for Vue 2, if you'd like to use in Vue 3 you will need to install the mixin.
} from '@unhead/vue'

// Apollo
import { split, ApolloClient, HttpLink, InMemoryCache, from } from '@apollo/client/core'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { createApolloProvider } from '@vue/apollo-option'

// Components
import SvgIcon from './components/icons/SvgIcon.vue'
import MainView from './main-view.vue'
import VueSelect from './plugins/vue-select/vue-select'
import FloatingVue from 'floating-vue'
// Import routes as a promise
import routesPromise from './router/routes'
import TripSettings from '@/components/trip-settings/trip-settings.vue'
import { removeTypenameFromMutationLink } from '/graphql/apollo-remove-typename-mutation-link';

// Settings
import settings from './settings.json';
// import customSettings from `./_customer/${_customer}/settings.json`;

const _customer = import.meta.env.VITE_CUSTOMER

interface MapTilesProvider {
  "name": String,
  "visible": Boolean,
  "attribution": String,
}

interface Settings {
  MAP_TILES_PROVIDER: MapTilesProvider,
  SUPPORTED_LOCALES: String,
  SHOW_TRIP_BADGES: Boolean,
  SHOW_LANGUAGE_SWITCH: Boolean,
  SIDEBAR_RESIZABLE: Boolean
}

const createAppSettings = async () => {
  let customSettings = {}

  try {
    const customSettingsModule = await import(`./_customer/${_customer}/settings.json`);

    customSettings = customSettingsModule.default || {};
  } catch (err) {
    console.error(err)
  }

  const appSettings: Settings = {...settings, ...customSettings}

  return appSettings;
};

// ==========================================================================
// = Create functions
// ==========================================================================
const apiUrl = import.meta.env.VITE_API_URL
const accessTokenKey = import.meta.env.VITE_API_BASIC_AUTH

function getHeaders() {
  const headers: { Authorization?: string; "Content-Type"?: string } = {};
  const token = accessTokenKey;
  if (token) {
    headers["Authorization"] = `Basic ${token}`;
  }
  headers["Content-Type"] = "application/json";

  return headers;
}

const httpLink = new HttpLink({
  // You should use an absolute URL here
  uri: `https://${apiUrl}/api/graphql`,
  fetch: (uri: RequestInfo, options: RequestInit) => {
    options.headers = getHeaders();
    return fetch(uri, options);
  }
})

const wsLink = new GraphQLWsLink(createClient({
  url: `wss://${apiUrl}/api/graphql`,
}));


// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

const link = from([removeTypenameFromMutationLink, splitLink]);

// Create the apollo client
export const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  connectToDevTools: true,
})

// Create a provider
const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
})

const tooltipOptions = {
  disposeTimeout: 200,
  themes: {
    tooltip: {
      delay: {
        show: 200,
        hide: 200
      }
    }
  }
}

// // ==========================================================================
// // = Bind components and functions and mount app
// // ==========================================================================

async function unmountYourneyApp(el: Element|string) {
  if (el && document.querySelector(el as string)?.__vue_app__) {
    await document.querySelector(el as string)?.__vue_app__.unmount()
  } else {
    return
  }
}

// Cancel token: each mountYourneyApp call increments this. A call that finds
// its token outdated (a newer call came in) aborts silently so only the latest
// mount wins. This prevents race conditions when Storyblok navigation triggers
// multiple rapid mount calls.
let _mountToken = 0

export async function mountYourneyApp(el: Element|string, opts: {
  initialRoute?: string,
} = {}) {
  const myToken = ++_mountToken

  try {
    await unmountYourneyApp(el)

    // A newer call came in while we were unmounting – abort.
    if (myToken !== _mountToken) return

    const App = createApp(MainView)
    const pinia = createPinia()
    const vfm = createVfm()
    const head = createHead()

    // ==========================================================================
    // = Bind components and functions and mount app
    // ==========================================================================

    App.component('TripSettingsModal', TripSettings);
    App.component('SvgIcon', SvgIcon);
    App.component('v-select', VueSelect)

    const routes = await routesPromise

    const routerOptions = {
      routes,
      history: createWebHistory()
    }

    const router = createRouter(routerOptions)

    if (opts.initialRoute) router.replace(opts.initialRoute)

    App.use(router)

    // prevent race condition where the app loads but the router hasnt been initialized
    await router.isReady()

    // A newer call came in while the router was initializing – abort.
    if (myToken !== _mountToken) return

    const appSettings = await createAppSettings()
    const i18n = setupI18n(appSettings)

    App.use(pinia)
    App.use(apolloProvider)
    App.use(vfm)
    App.use(FloatingVue, tooltipOptions)
    App.use(head)
    App.mixin(VueHeadMixin)

    App.use(i18n)
    App.provide('appSettings', appSettings)

    App.mount(el)

    return {
      App,
      router,
      navigate: (path: string) => router.push(path),
      destroy: () => App.unmount()
    }
  } catch (err) {
    console.error('[yourney] Failed to initialize app:', err)

    const container = typeof el === 'string'
      ? document.querySelector(el)
      : el as HTMLElement

    if (container) {
      (container as HTMLElement).innerHTML =
        '<div style="padding:1rem;font-family:sans-serif">Failed to load application. Please refresh the page.</div>'
    }
  }
}

Object.assign(window as any, { mountYourneyApp })
